import dayjs from "dayjs";
import { FC, useState } from "react";
import { useLogeMembers } from "../../api/user-api";
import { Fine } from "../../types/entities";
import { Icon } from "./Icon";

const PAYMENT_METHODS: { key: NonNullable<Fine["paid_via"]>; label: string }[] = [
  { key: "mobilepay", label: "MOBILEPAY" },
  { key: "cash", label: "KONTANT" },
  { key: "bank", label: "BANK" },
];

const MarkPaidSheet: FC<{
  fine: Fine;
  onConfirm: (paidVia: Fine["paid_via"]) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}> = ({ fine, onConfirm, onCancel, isSubmitting }) => {
  const { data: members } = useLogeMembers();
  const who = members?.find((m) => m.id === fine.loge_member_id)?.name ?? "—";
  const [paidVia, setPaidVia] = useState<Fine["paid_via"]>("mobilepay");

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/60" onClick={onCancel}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-paper text-ink rounded-t-lg border border-ink p-5"
        // max, not +: the home indicator's clearance already is the gap this
        // wanted, and stacking the two left a slab of empty sheet below the
        // buttons once the app was installed.
        style={{ paddingBottom: "max(1.5rem, var(--safe-bottom))" }}
      >
        <div className="flex justify-center mb-3.5">
          <div className="w-9 h-[3px] bg-line rounded-full" />
        </div>
        <div className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-ink3">[ Markér betalt ]</div>
        <div className="font-display text-xl font-extrabold uppercase tracking-tight mt-1.5">
          {who} — {fine.reason.toLowerCase()}
        </div>
        <div className="font-mono text-[10.5px] text-ink3 tracking-wide mt-1">
          Udstedt {dayjs(fine.created_at).format("D. MMM")}
        </div>

        <div className="mt-4.5 py-3.5 px-4 rounded-lg bg-ink text-paper flex items-baseline justify-between">
          <div className="font-mono text-[10px] tracking-wide text-white/55">BELØB</div>
          <div className="font-display font-extrabold text-2xl tracking-tight tabular-nums">
            {fine.amount_kr}
            <span className="font-mono text-xs font-medium text-white/55 ml-1">KR</span>
          </div>
        </div>

        <div className="mt-4">
          <div className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-ink3 mb-2">
            [ Modtaget via ]
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {PAYMENT_METHODS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setPaidVia(opt.key)}
                className={`py-2.5 text-center rounded-lg border font-mono text-[10.5px] font-semibold tracking-wide ${
                  paidVia === opt.key ? "bg-ink border-ink text-paper" : "border-line text-ink2"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4.5 flex flex-col gap-2">
          <button
            disabled={isSubmitting}
            onClick={() => onConfirm(paidVia)}
            className="w-full py-3.5 px-4 rounded-lg bg-vote-yes text-paper font-display text-sm font-bold uppercase tracking-wide flex items-center justify-between gap-3"
          >
            <span className="inline-flex items-center gap-2">
              <Icon name="check" size={16} /> Bekræft betaling
            </span>
            <span className="font-mono text-[11px] opacity-70">
              {fine.amount_kr} KR
            </span>
          </button>
          <button
            onClick={onCancel}
            className="w-full py-3 rounded-lg border border-line font-mono text-[11px] font-semibold uppercase tracking-wide"
          >
            Annullér
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarkPaidSheet;
